import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wrench,
  MagnifyingGlass,
  ArrowClockwise,
  CaretLeft,
  Camera,
  CurrencyDollar,
  Diamond,
  Buildings,
  UsersThree,
  CircleNotch,
  Check,
  ShieldStar,
  ShieldSlash,
  Trash,
  Broom,
  ClipboardText,
  Newspaper,
} from "@phosphor-icons/react";
import {
  devListUsers,
  devUserDetail,
  devGrant,
  devTakeSnapshot,
  devRestoreSnapshot,
  devSetRole,
  devDeleteUser,
  devCleanupEmptyAlliances,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import MissionsAdminDialog from "@/components/MissionsAdminDialog";
import EventsAdminDialog from "@/components/EventsAdminDialog";
import NewsAdminDialog from "@/components/NewsAdminDialog";

// "Active now" pill — last_seen_at within 5 min as enforced by the server.
function StatusDot({ online }) {
  return (
    <span
      title={online ? "Active now" : "Offline"}
      className={`inline-block w-2 h-2 rounded-full ${online ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]" : "bg-zinc-600"}`}
    />
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  const days = Math.round(m / 1440);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function UserListRow({ row, onSelect }) {
  const initial = (row.name || "?").slice(0, 1).toUpperCase();
  return (
    <button
      data-testid={`dev-user-row-${row.user_id}`}
      type="button"
      onClick={() => onSelect(row)}
      className="w-full grid grid-cols-12 items-center gap-2 px-3 py-2 border-b border-white/5 hover:bg-white/[0.04] text-left transition-colors"
    >
      <span className="col-span-1 flex items-center gap-1.5">
        <StatusDot online={row.online} />
        <span className="w-5 h-5 inline-flex items-center justify-center bg-white/10 text-zinc-300 mono text-[9px] uppercase">
          {initial}
        </span>
      </span>
      <span className="col-span-4 truncate" title={`${row.name} · ${row.email}`}>
        <span className="text-zinc-100 text-[12px]">{row.name}</span>
        <span className="text-zinc-500 mono text-[9px] uppercase ml-1.5 truncate">{row.email}</span>
      </span>
      <span className="col-span-2 mono text-[11px] text-emerald-300 truncate">
        ${row.money.toLocaleString()}
      </span>
      <span className="col-span-1 mono text-[11px] text-cyan-300">
        <Diamond size={9} weight="fill" className="inline mr-0.5" />
        {row.gems.toLocaleString()}
      </span>
      <span className="col-span-1 mono text-[11px] text-zinc-300">
        <Buildings size={9} className="inline mr-0.5" />
        {row.stations_count}
      </span>
      <span className="col-span-2 mono text-[10px] text-zinc-500 truncate">
        {row.alliance ? row.alliance.name : "—"}
      </span>
      <span className="col-span-1 mono text-[9px] text-zinc-500 uppercase tracking-widest truncate text-right">
        {fmtTime(row.last_seen_at)}
      </span>
    </button>
  );
}

function GrantForm({ uid, onApplied }) {
  const [money, setMoney] = useState("");
  const [gems, setGems] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = useCallback(async () => {
    const m = parseInt(money || "0", 10);
    const g = parseInt(gems || "0", 10);
    if (!Number.isFinite(m) || !Number.isFinite(g)) {
      toast.error("Enter integer amounts");
      return;
    }
    if (m === 0 && g === 0) {
      toast.error("Specify money and/or gems");
      return;
    }
    setBusy(true);
    try {
      const r = await devGrant(uid, { money: m, gems: g, note });
      toast.success(`Granted${m ? ` $${m.toLocaleString()}` : ""}${g ? ` ${g} gems` : ""}`);
      setMoney(""); setGems(""); setNote("");
      onApplied?.(r);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Grant failed");
    } finally {
      setBusy(false);
    }
  }, [money, gems, note, uid, onApplied]);

  return (
    <div className="border border-white/10 bg-white/[0.02] p-3 space-y-2">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
        Grant resources (use negative to deduct)
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">
            Money Δ
          </span>
          <Input
            data-testid="dev-grant-money"
            type="number"
            value={money}
            onChange={(e) => setMoney(e.target.value)}
            placeholder="e.g. 50000"
            className="rounded-none h-8 bg-black/40 border-white/10 mono text-[11px]"
          />
        </label>
        <label className="block">
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">
            Gems Δ
          </span>
          <Input
            data-testid="dev-grant-gems"
            type="number"
            value={gems}
            onChange={(e) => setGems(e.target.value)}
            placeholder="e.g. 100"
            className="rounded-none h-8 bg-black/40 border-white/10 mono text-[11px]"
          />
        </label>
      </div>
      <label className="block">
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">
          Note (audit log)
        </span>
        <Input
          data-testid="dev-grant-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why? — visible in audit log"
          className="rounded-none h-8 bg-black/40 border-white/10 mono text-[11px]"
        />
      </label>
      <div className="flex justify-end">
        <Button
          data-testid="dev-grant-apply"
          disabled={busy}
          onClick={apply}
          size="sm"
          className="rounded-none h-8 px-3 mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black border border-emerald-400"
        >
          {busy ? <CircleNotch className="animate-spin" size={12} /> : <><Check size={12} className="mr-1" /> Apply</>}
        </Button>
      </div>
    </div>
  );
}

function SnapshotList({ uid, snapshots, onRefresh }) {
  const [restoring, setRestoring] = useState(null);
  const [snapping, setSnapping] = useState(false);

  const takeNow = async () => {
    setSnapping(true);
    try {
      await devTakeSnapshot(uid);
      toast.success("Snapshot captured");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Snapshot failed");
    } finally {
      setSnapping(false);
    }
  };

  const restore = async (snap) => {
    const ok = window.confirm(
      `Restore this user's game state to the snapshot from ${new Date(snap.captured_at).toLocaleString()}?\n\n` +
      `A safety pre_restore snapshot will be taken automatically first.`
    );
    if (!ok) return;
    setRestoring(snap.snapshot_id);
    try {
      const r = await devRestoreSnapshot(uid, snap.snapshot_id);
      toast.success(`Restored from ${new Date(r.restored_from).toLocaleString()}`);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">
          Snapshots ({snapshots.length})
        </div>
        <Button
          data-testid="dev-snapshot-take"
          size="sm"
          disabled={snapping}
          onClick={takeNow}
          variant="outline"
          className="rounded-none h-7 px-2 mono uppercase text-[9px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5"
        >
          <Camera size={11} className="mr-1" />
          {snapping ? "…" : "Take Snapshot"}
        </Button>
      </div>
      {snapshots.length === 0 ? (
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 py-3 text-center">
          No snapshots yet. Auto-snapshots run weekly on user activity.
        </div>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          {snapshots.map((s) => (
            <div
              key={s.snapshot_id}
              data-testid={`dev-snapshot-${s.snapshot_id}`}
              className="grid grid-cols-12 items-center px-1 py-1.5 border-b border-white/5"
            >
              <span className="col-span-2 mono text-[9px] uppercase tracking-widest"
                style={{
                  color: s.kind === "manual" ? "#fbbf24" : s.kind === "pre_restore" ? "#f87171" : "#a3a3a3",
                }}
              >
                {s.kind}
              </span>
              <span className="col-span-6 mono text-[11px] text-zinc-200">
                {new Date(s.captured_at).toLocaleString()}
              </span>
              <span className="col-span-4 text-right">
                <Button
                  data-testid={`dev-restore-${s.snapshot_id}`}
                  size="sm"
                  disabled={restoring === s.snapshot_id}
                  onClick={() => restore(s)}
                  className="rounded-none h-6 px-2 mono uppercase text-[9px] tracking-widest bg-amber-500/15 text-amber-300 border border-amber-400/40 hover:bg-amber-500/25"
                >
                  {restoring === s.snapshot_id ? "…" : <><ArrowClockwise size={10} className="mr-1" /> Restore</>}
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UserDetail({ uid, onBack, onUserDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleBusy, setRoleBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user: me } = useAuth();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await devUserDetail(uid);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load user");
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500 mono text-[11px] uppercase tracking-widest">
        <CircleNotch className="animate-spin mr-2" /> Loading user…
      </div>
    );
  }

  const u = data.user;
  const s = data.state || {};
  const stations = s.stations || [];
  const hospitals = s.hospitals || [];
  const isDeveloper = (u.roles || []).includes("developer");
  const isSelf = me?.user_id === u.user_id;

  const toggleDeveloper = async () => {
    const action = isDeveloper ? "revoke" : "grant";
    const verb = isDeveloper ? "Revoke developer role" : "Promote to developer";
    if (!window.confirm(`${verb} for ${u.name} (${u.email})?`)) return;
    setRoleBusy(true);
    try {
      await devSetRole(u.user_id, "developer", !isDeveloper);
      toast.success(isDeveloper ? "Developer role revoked" : "Promoted to developer");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `Could not ${action} role`);
    } finally {
      setRoleBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Type-to-confirm — destructive operation. Two prompts: warning + email match.
    if (!window.confirm(
      `⚠️ DELETE THIS ACCOUNT?\n\n` +
      `User: ${u.name} (${u.email})\n\n` +
      `This will permanently remove their game state, stations, hospitals, alliance memberships, chat history, payment records, and any other associated data. THIS CANNOT BE UNDONE.\n\n` +
      `Continue?`
    )) return;
    const typed = window.prompt(
      `Type the email exactly to confirm:\n\n${u.email}`
    );
    if (typed?.trim().toLowerCase() !== (u.email || "").toLowerCase()) {
      toast.error("Email did not match. Deletion cancelled.");
      return;
    }
    setDeleting(true);
    try {
      await devDeleteUser(u.user_id);
      toast.success(`Deleted ${u.name} (${u.email})`);
      onUserDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not delete user");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="dev-user-detail">
      <div className="flex items-center justify-between">
        <Button
          data-testid="dev-back-to-list"
          size="sm"
          variant="ghost"
          onClick={onBack}
          className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest text-zinc-400 hover:text-white"
        >
          <CaretLeft size={12} className="mr-1" /> Back to list
        </Button>
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
          <StatusDot online={u.online} />
          <span className="text-zinc-400">
            {u.online ? "Active now" : `Last seen ${fmtTime(u.last_seen_at)}`}
          </span>
        </div>
      </div>

      <div className="border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-zinc-100 text-base font-semibold">{u.name}</div>
            <div className="mono text-[10px] text-zinc-500 truncate">{u.email}</div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
              {u.user_id} · {u.auth_provider}
              {u.roles?.length > 0 && (
                <span className="ml-2 text-amber-300">[{u.roles.join(", ")}]</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 items-end">
            <Button
              data-testid={isDeveloper ? "dev-revoke-developer" : "dev-promote-developer"}
              size="sm"
              variant="outline"
              disabled={roleBusy || (isSelf && isDeveloper)}
              onClick={toggleDeveloper}
              title={isSelf && isDeveloper ? "You cannot revoke your own developer role" : ""}
              className={`rounded-none h-8 px-3 mono uppercase text-[10px] tracking-widest border ${
                isDeveloper
                  ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
                  : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              }`}
            >
              {roleBusy ? (
                <CircleNotch size={11} className="animate-spin mr-1.5" />
              ) : isDeveloper ? (
                <ShieldSlash size={11} className="mr-1.5" />
              ) : (
                <ShieldStar size={11} weight="fill" className="mr-1.5" />
              )}
              {isDeveloper ? "Revoke Dev" : "Promote to Dev"}
            </Button>
            <Button
              data-testid="dev-delete-user"
              size="sm"
              variant="outline"
              disabled={deleting || isSelf}
              onClick={handleDeleteAccount}
              title={isSelf ? "You cannot delete your own account" : "Permanently delete this account"}
              className="rounded-none h-8 px-3 mono uppercase text-[10px] tracking-widest border border-red-600/60 text-red-300 hover:bg-red-600/15 disabled:opacity-40"
            >
              {deleting ? (
                <CircleNotch size={11} className="animate-spin mr-1.5" />
              ) : (
                <Trash size={11} weight="fill" className="mr-1.5" />
              )}
              Delete Account
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3 text-center">
          <div className="border border-emerald-500/20 bg-emerald-500/5 py-2">
            <div className="mono text-[9px] uppercase tracking-widest text-emerald-400">Money</div>
            <div className="mono text-sm font-bold text-emerald-300" data-testid="dev-detail-money">
              ${(s.money || 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-cyan-500/20 bg-cyan-500/5 py-2">
            <div className="mono text-[9px] uppercase tracking-widest text-cyan-400">Gems</div>
            <div className="mono text-sm font-bold text-cyan-300" data-testid="dev-detail-gems">
              <Diamond size={11} weight="fill" className="inline -mt-0.5 mr-0.5" />
              {(s.gems || 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-white/10 py-2">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">XP</div>
            <div className="mono text-sm font-bold text-zinc-200">
              {(s.xp || 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-pink-500/20 bg-pink-500/5 py-2">
            <div className="mono text-[9px] uppercase tracking-widest text-pink-400">Premium</div>
            <div className="mono text-[10px] font-bold text-pink-300">
              {s.premium_until ? new Date(s.premium_until).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="border border-white/5 py-2 text-center">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Stations</div>
            <div className="mono text-[12px] text-zinc-200">{stations.length}</div>
          </div>
          <div className="border border-white/5 py-2 text-center">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Hospitals</div>
            <div className="mono text-[12px] text-zinc-200">{hospitals.length}</div>
          </div>
          <div className="border border-white/5 py-2 text-center">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Alliance</div>
            <div className="mono text-[10px] text-orange-300 truncate" title={data.alliance?.name}>
              {data.alliance ? data.alliance.name : "—"}
            </div>
          </div>
        </div>
      </div>

      <GrantForm uid={uid} onApplied={refresh} />

      <SnapshotList uid={uid} snapshots={data.snapshots || []} onRefresh={refresh} />

      {(data.audits || []).length > 0 && (
        <div className="border border-white/10 bg-white/[0.02] p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
            Recent audit log (last 20)
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {data.audits.map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 mono text-[10px] py-1 border-b border-white/5">
                <span className="col-span-3 text-zinc-500">{new Date(a.at).toLocaleString()}</span>
                <span className="col-span-2 text-amber-300 uppercase">{a.action}</span>
                <span className="col-span-3 text-zinc-300 truncate">{a.actor_name}</span>
                <span className="col-span-4 text-zinc-400 truncate" title={a.note || ""}>
                  {a.action === "grant"
                    ? `${a.money_delta ? `$${a.money_delta}` : ""} ${a.gems_delta ? `${a.gems_delta} gems` : ""}`
                    : a.snapshot_id || ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Developer panel — visible only when current user has the `developer` role.
 * Provides admin tools for inspecting + adjusting any user account.
 */
export default function DeveloperDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedUid, setSelectedUid] = useState(null);
  const [cleaningAlliances, setCleaningAlliances] = useState(false);

  const handleCleanupEmptyAlliances = async () => {
    if (!window.confirm(
      "Delete every Mutual Aid Group that currently has 0 members?\n\n" +
      "Their chat history and any open shared calls will be wiped as well."
    )) return;
    setCleaningAlliances(true);
    try {
      const r = await devCleanupEmptyAlliances();
      if (r.deleted_count === 0) {
        toast.success("No empty groups to clean up");
      } else {
        toast.success(`Deleted ${r.deleted_count} empty group(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Cleanup failed");
    } finally {
      setCleaningAlliances(false);
    }
  };

  // Debounce search input so we don't flood the backend while typing.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const d = await devListUsers({ q: debouncedQ, sort, limit: 100 });
      setUsers(d.users || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load users");
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, sort]);

  useEffect(() => {
    if (open && !selectedUid) fetchUsers();
  }, [open, selectedUid, fetchUsers]);

  // Reset selection when dialog closes — fresh state each open.
  useEffect(() => {
    if (!open) {
      setSelectedUid(null);
      setQuery("");
      setSort("recent");
    }
  }, [open]);

  const totalsLabel = useMemo(() => {
    const online = users.filter((u) => u.online).length;
    return `${total.toLocaleString()} users · ${online} active`;
  }, [users, total]);

  const [missionsOpen, setMissionsOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="developer-dialog"
        className="rounded-none border border-amber-500/30 bg-[#0A0E14] text-white max-w-4xl p-0 max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-amber-500/20 bg-amber-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-amber-300 flex items-center gap-2">
            <Wrench size={14} weight="fill" /> Developer Panel
            <span className="text-zinc-500 ml-2 text-[10px] tracking-wider font-normal">
              {totalsLabel}
            </span>
            <span className="flex-1" />
            <Button
              data-testid="dev-missions-btn"
              size="sm"
              variant="outline"
              onClick={() => setMissionsOpen(true)}
              className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 mr-2"
            >
              <ClipboardText size={11} className="mr-1.5" />
              Missions
            </Button>
            <Button
              data-testid="dev-events-btn"
              size="sm"
              variant="outline"
              onClick={() => setEventsOpen(true)}
              className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 mr-2"
            >
              <ClipboardText size={11} className="mr-1.5" />
              Events
            </Button>
            <Button
              data-testid="dev-news-btn"
              size="sm"
              variant="outline"
              onClick={() => setNewsOpen(true)}
              className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 mr-2"
            >
              <Newspaper size={11} className="mr-1.5" />
              News
            </Button>
            <Button
              data-testid="dev-cleanup-empty-alliances"
              size="sm"
              variant="outline"
              disabled={cleaningAlliances}
              onClick={handleCleanupEmptyAlliances}
              className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 mr-6"
            >
              {cleaningAlliances ? (
                <CircleNotch size={11} className="animate-spin mr-1.5" />
              ) : (
                <Broom size={11} className="mr-1.5" />
              )}
              Cleanup Empty Groups
            </Button>
          </DialogTitle>
        </DialogHeader>
        <MissionsAdminDialog open={missionsOpen} onOpenChange={setMissionsOpen} />
        <NewsAdminDialog open={newsOpen} onOpenChange={setNewsOpen} />
        <EventsAdminDialog open={eventsOpen} onOpenChange={setEventsOpen} />

        <div className="overflow-y-auto px-5 py-3">
          {selectedUid ? (
            <UserDetail
              uid={selectedUid}
              onBack={() => { setSelectedUid(null); fetchUsers(); }}
              onUserDeleted={() => { setSelectedUid(null); fetchUsers(); }}
            />
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-[#0A0E14] py-1 z-10">
                <div className="relative flex-1">
                  <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    data-testid="dev-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    className="rounded-none h-8 pl-7 bg-black/40 border-white/10 mono text-[11px]"
                  />
                </div>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger
                    data-testid="dev-sort-select"
                    className="w-44 rounded-none h-8 bg-black/40 border-white/10 mono text-[10px] uppercase tracking-widest"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#0A0E14] border-white/15 text-white">
                    <SelectItem value="recent" className="rounded-none mono text-[11px] tracking-wider">Recent activity</SelectItem>
                    <SelectItem value="money" className="rounded-none mono text-[11px] tracking-wider">Most Money</SelectItem>
                    <SelectItem value="gems" className="rounded-none mono text-[11px] tracking-wider">Most Gems</SelectItem>
                    <SelectItem value="stations" className="rounded-none mono text-[11px] tracking-wider">Most Stations</SelectItem>
                    <SelectItem value="name" className="rounded-none mono text-[11px] tracking-wider">Name (A→Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-12 px-3 py-1 mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <span className="col-span-1">●</span>
                <span className="col-span-4">User</span>
                <span className="col-span-2"><CurrencyDollar size={9} className="inline" /> Money</span>
                <span className="col-span-1"><Diamond size={9} className="inline" /> Gems</span>
                <span className="col-span-1"><Buildings size={9} className="inline" /> Stns</span>
                <span className="col-span-2"><UsersThree size={9} className="inline" /> Alliance</span>
                <span className="col-span-1 text-right">Seen</span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12 text-zinc-500 mono text-[11px] uppercase tracking-widest">
                  <CircleNotch className="animate-spin mr-2" /> Loading…
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-10 mono text-[11px] uppercase tracking-widest text-zinc-500">
                  No users found
                </div>
              ) : (
                <div>
                  {users.map((u) => (
                    <UserListRow key={u.user_id} row={u} onSelect={(r) => setSelectedUid(r.user_id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
